import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api/client';

interface AlertaHistorico {
  importacao_id: string;
  status: string;
  tipo: string;
  arquivo: string;
  total_registros: number;
  registros_ok: number;
  created_at: string;
}

interface AlertaRegraSummary {
  id: string;
  nome: string;
  disparado: boolean;
  valor: number;
  created_at: string;
}

interface Props {
  empresa_id: string;
}

export function AlertasBadge({ empresa_id }: Props) {
  const { data: importacoes, isLoading } = useQuery({
    queryKey: ['importacoes', empresa_id],
    queryFn: () =>
      api.get<AlertaHistorico[]>('/api/importacao/historico/', { params: { empresa_id } }).then((r) => r.data),
    enabled: !!empresa_id,
    refetchInterval: 30_000,
  });

  const pendentes = importacoes?.filter((i) => i.status === 'processando').length ?? 0;
  const erros = importacoes?.filter((i) => i.status === 'erro').length ?? 0;

  if (isLoading) {
    return <div style={styles.wrapper}><span style={styles.badge}>...</span></div>;
  }

  return (
    <div style={styles.wrapper}>
      <span style={styles.titulo}>Importações</span>
      <div style={styles.badges}>
        {pendentes > 0 && (
          <span style={{ ...styles.badge, background: '#f0a500' }}>
            ⟳ {pendentes} processando
          </span>
        )}
        {erros > 0 && (
          <span style={{ ...styles.badge, background: '#E24B4A' }}>
            ✗ {erros} com erro
          </span>
        )}
        {pendentes === 0 && erros === 0 && (
          <span style={{ ...styles.badge, background: '#48d951' }}>
            ✓ OK
          </span>
        )}
      </div>
      {importacoes && importacoes.length > 0 && (
        <div style={styles.lista}>
          {importacoes.slice(0, 5).map((imp) => (
            <div key={imp.importacao_id} style={styles.item}>
              <span style={styles.itemTipo}>{imp.tipo.replace(/_/g, ' ')}</span>
              <span style={{ ...styles.itemStatus, color: _corStatus(imp.status) }}>
                {imp.status}
              </span>
              <span style={styles.itemData}>
                {new Date(imp.created_at).toLocaleDateString('pt-BR')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function _corStatus(status: string) {
  if (status === 'concluido') return '#48d951';
  if (status === 'erro') return '#E24B4A';
  if (status === 'processando') return '#f0a500';
  return '#94a3b8';
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    background: '#1e2130',
    borderRadius: 12,
    padding: 20,
    color: '#e2e8f0',
    fontFamily: 'Inter, system-ui, sans-serif',
    minWidth: 240,
  },
  titulo: { display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 12, fontWeight: 500 },
  badges: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  badge: {
    fontSize: 11,
    padding: '4px 10px',
    borderRadius: 20,
    color: '#fff',
    fontWeight: 600,
    background: '#2D73F5',
  },
  lista: { display: 'flex', flexDirection: 'column', gap: 6 },
  item: { display: 'flex', justifyContent: 'space-between', fontSize: 11, gap: 8 },
  itemTipo: { color: '#cbd5e1', flex: 1 },
  itemStatus: { fontWeight: 600, textTransform: 'capitalize' },
  itemData: { color: '#64748b' },
};
